import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../api';
import { uploadImage } from '../services/imageUpload';
import * as analytics from '../services/analytics';
import { Camera, Loader2, X, Lightbulb, Plus, ImagePlus, Calendar } from 'lucide-react';

const inp = "w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:border-[#004B00] focus:ring-2 focus:ring-[#004B00]/20 outline-none font-nunito text-sm bg-white transition-all";

const COMMON_TOOLS = ['Drill','Saw','Hammer','Ladder','Lawn Mower','Pressure Washer','Sander','Wrench Set','Level','Tape Measure','Tile Cutter','Paint Sprayer'];

const PROJECT_CATEGORIES = [
  'Home Improvement','Gardening & Landscaping','Woodworking & Crafts',
  'Plumbing & Electrical','Painting & Decorating','Moving & Hauling',
  'Cooking & Baking','Event Planning & Hosting','Automotive',
  'Arts & Creative','Community Service','Technology & Electronics',
  'Sports & Recreation','Education & Learning','Other',
];

const MAX_PHOTOS = 4;

export default function ProjectFormModal({ isOpen, onClose, onSave, editProject = null, prefillContext = null }) {
  const emptyForm = { title: '', description: '', photos: [], tools_needed: [], categories: [], status: 'active', start_date: '', end_date: '' };

  const [form, setForm] = useState(emptyForm);
  const [toolInput, setToolInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (editProject) {
      setForm({
        title: editProject.title,
        description: editProject.description,
        photos: editProject.photos?.length ? [...editProject.photos] : (editProject.photo ? [editProject.photo] : []),
        tools_needed: [...(editProject.tools_needed || [])],
        categories: [...(editProject.categories || [])],
        status: editProject.status,
        start_date: editProject.start_date || '',
        end_date: editProject.end_date || '',
      });
    } else if (prefillContext?.tool) {
      setForm({ ...emptyForm, tools_needed: [prefillContext.tool] });
    } else {
      setForm(emptyForm);
    }
    setToolInput('');
  }, [isOpen, editProject]);

  const handlePhotoAdd = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (form.photos.length >= MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos allowed`);
      e.target.value = '';
      return;
    }
    try {
      const { url } = await uploadImage(file);
      setForm(f => ({ ...f, photos: [...f.photos, url] }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Photo upload failed');
    } finally {
      e.target.value = '';
    }
  };

  const removePhoto = (idx) => setForm(f => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const addTool = () => {
    if (toolInput.trim() && !form.tools_needed.includes(toolInput.trim())) {
      setForm(f => ({ ...f, tools_needed: [...f.tools_needed, toolInput.trim()] }));
      setToolInput('');
    }
  };

  const removeTool = (t) => setForm(f => ({ ...f, tools_needed: f.tools_needed.filter(x => x !== t) }));

  const toggleCategory = (cat) => setForm(f => ({
    ...f,
    categories: f.categories.includes(cat) ? f.categories.filter(c => c !== cat) : [...f.categories, cat],
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error('Anticipated end date must be on or after the start date');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        photo: form.photos[0] || '',
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      };
      let res;
      if (editProject) {
        res = await api.put(`/projects/${editProject.id}`, payload);
      } else {
        res = await api.post('/projects', payload);
        analytics.track('project_created', { categories: (form.categories || []).slice(0, 3).join(',') });
      }
      toast.success(editProject ? 'Project updated!' : 'Project created!');
      onSave(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-stone-100">
          <h3 className="text-lg font-poppins font-semibold">{editProject ? 'Edit Project' : 'New Project'}</h3>
          <button onClick={onClose}><X size={20} className="text-gray-400 hover:text-gray-600" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {prefillContext && (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
              <Lightbulb size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-nunito text-amber-800">
                You're creating a project to request <strong>{prefillContext.tool}</strong>
                {prefillContext.from && <> from <strong>{prefillContext.from}</strong></>}.
                {' '}Once created, they'll be matched to your project.
              </p>
            </div>
          )}

          {/* Multi-image upload (up to 4) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 font-nunito">
              Photos <span className="text-gray-400 font-normal">({form.photos.length}/{MAX_PHOTOS})</span>
            </label>
            <div className="grid grid-cols-4 gap-2">
              {form.photos.map((src, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-stone-200 group">
                  <img src={src} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={12} />
                  </button>
                  {i === 0 && (
                    <span className="absolute bottom-1 left-1 text-[10px] bg-[#004B00]/80 text-white px-1.5 py-0.5 rounded-full font-nunito">Cover</span>
                  )}
                </div>
              ))}
              {form.photos.length < MAX_PHOTOS && (
                <label className="aspect-square rounded-xl border-2 border-dashed border-stone-200 hover:border-[#004B00] flex flex-col items-center justify-center cursor-pointer text-stone-400 hover:text-[#004B00] transition-all">
                  <ImagePlus size={22} />
                  <span className="text-[10px] font-nunito mt-1">Add photo</span>
                  <input type="file" accept="image/*" onChange={handlePhotoAdd} className="hidden" />
                </label>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-nunito">Project Title *</label>
            <input required className={inp} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Fix the back fence, Build raised garden bed..." />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-nunito">Description</label>
            <textarea rows={3} className={`${inp} resize-none`} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe what you're working on..." />
          </div>

          {/* Timeline */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-nunito flex items-center gap-1.5">
              <Calendar size={14} className="text-[#004B00]"/> Timeline <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1 font-nunito">Start date</label>
                <input
                  type="date"
                  className={inp}
                  value={form.start_date}
                  onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                  data-testid="project-start-date"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1 font-nunito">Anticipated end date</label>
                <input
                  type="date"
                  className={inp}
                  value={form.end_date}
                  onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                  min={form.start_date || undefined}
                  data-testid="project-end-date"
                />
              </div>
            </div>
            <p className="text-[11px] text-gray-400 font-nunito mt-1">Helps neighbors know when you need tools by.</p>
          </div>

          {/* Categories */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 font-nunito">Categories</label>
            <div className="grid grid-cols-2 gap-1.5">
              {PROJECT_CATEGORIES.map(cat => (
                <label key={cat} data-testid={`category-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                  className={`flex items-center gap-2.5 cursor-pointer p-2.5 rounded-lg border transition-all ${form.categories.includes(cat) ? 'border-[#004B00] bg-[#004B00]/5' : 'border-stone-200 hover:border-stone-300'}`}>
                  <input type="checkbox" checked={form.categories.includes(cat)} onChange={() => toggleCategory(cat)} className="accent-[#004B00] w-3.5 h-3.5 flex-shrink-0" />
                  <span className={`font-nunito text-xs leading-tight ${form.categories.includes(cat) ? 'text-[#004B00] font-semibold' : 'text-gray-600'}`}>{cat}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Tools Needed */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-nunito">Tools Needed</label>
            <div className="flex gap-2 mb-2">
              <input className={inp} value={toolInput} onChange={e => setToolInput(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addTool())}
                placeholder="Add a tool..." />
              <button type="button" onClick={addTool} className="bg-[#004B00] text-white px-4 py-2 rounded-lg font-nunito font-semibold text-sm hover:bg-[#003800] transition-all whitespace-nowrap">Add</button>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {COMMON_TOOLS.map(t => (
                <button key={t} type="button"
                  onClick={() => { if (!form.tools_needed.includes(t)) setForm(f => ({ ...f, tools_needed: [...f.tools_needed, t] })); }}
                  className={`text-xs px-2.5 py-1 rounded-full font-nunito transition-all border ${form.tools_needed.includes(t) ? 'bg-[#004B00] text-white border-[#004B00]' : 'bg-stone-100 text-gray-600 hover:bg-stone-200 border-stone-200'}`}>
                  {t}
                </button>
              ))}
            </div>
            {form.tools_needed.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {form.tools_needed.map(t => (
                  <span key={t} className="bg-green-50 text-[#004B00] border border-green-200 text-xs px-2.5 py-1 rounded-full font-nunito flex items-center gap-1">
                    {t}<button type="button" onClick={() => removeTool(t)}><X size={10} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border-2 border-stone-200 text-gray-700 py-3 rounded-xl font-nunito font-semibold hover:bg-gray-50 transition-all">Cancel</button>
            <button type="submit" disabled={saving} data-testid="save-project-btn"
              className="flex-1 bg-[#004B00] hover:bg-[#003800] text-white py-3 rounded-xl font-nunito font-semibold transition-all disabled:opacity-60 flex items-center justify-center gap-2">
              {saving && <Loader2 size={16} className="animate-spin" />}
              {editProject ? 'Update Project' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
